/** @jsxImportSource @emotion/react */
import { faCheckCircle, faFileExport, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { useIsMutating } from "@tanstack/react-query";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { triggerCheckBusinessRules } from "../../../../../actions/Checks/actions";
import { applyDisplayFilterFields } from "../../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { path_summary } from "../../../../../actions/Tenants/config/constants";
import { FormField, UserRole } from "../../../../../actions/Tenants/config/constantsTyped";
import { AvosAction } from "../../../../../actions/types";
import { useSendEmailNotificationMutation } from "../../../../../hooks/mutations/useCheckMutations";
import { useCheckImages } from "../../../../../hooks/useCheckImages";
import { useConnectivity } from "../../../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../../../offline/db";
import {
    getActiveSessionId,
    markReadyToSync,
} from "../../../../../offline/sessionManager";
import { useOfflineFruitImages } from "../../../../../offline/useOfflineFruitImages";
import { RootState } from "../../../../../reducers";
import useAuthorization from "../../../../../utils/authorization";
import { validateRequiredImages } from "../../../../../utils/imageValidation";
import { toast } from "../../../../../utils/toast";
import { callAvosApi } from "../../../../../utils/useAvosApiHook";
import { metaFormIsValid } from "../../../../Forms/MetaForm";
import { Button } from "../../../../Helper/Button";
import { Loading } from "../../../../Helper/Loading";
import ScanLayerButton from "../../../Layers/ScanLayerButton";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";
import Box from "../Layout";
import BoxedFruitTable, { BoxItem } from "./Boxes";
import CheckImages from "./CheckImages";
import CheckSummaryForm from "./CheckSummaryForm";
import InvalidCheckModal from "./InvalidCheckModal";
import LayerCtaCheckSummary from "./LayerCtaCheckSummary";
import PalletSummary from "./PalletSummary";
import PreCheckFormModal from "./PreCheckFormModal";
import RetrospectiveGroupingModal from "./RetrospectiveGroupingModal";
import SmartSamplingHistogram from "./SmartSamplingHistogram";

import { useRouteCheck, useRouteLayer } from "../../../../../hooks/useCurrentEntities";

// * Shared by Submit and Next-pallet so neither can complete a check while its summary form/images are invalid.
export function useCheckSummaryValidation(layer: any, check: any) {
    const params: any = useParams();
    const config = useConfig();
    const location = config.get_check_type_config(layer, check);
    const required_images = config.get_check_required_images(layer, check);
    const currentFlow = location?.flow?.find((f) => f.path === params.action);
    const flowHasRequiredImages = !currentFlow?.disable_required_images;
    const { data: images = [] } = useCheckImages(params.tenant_id, check.check_id);

    const validate = (show_toast = true) => {
        const metaFormValid = !location?.meta_form_summary || metaFormIsValid(location.meta_form_summary as FormField[], check, show_toast);
        const imagesValid = !flowHasRequiredImages || validateRequiredImages(required_images, images || [], show_toast);
        return metaFormValid && imagesValid;
    };

    return { location, required_images, flowHasRequiredImages, images, validate };
}

export default function CheckSummary() {
    const params: any = useParams();
    const tenantId = params.tenant_id as string;
    const dispatch = useDispatch();
    const { navigateToCheckAction, exitCheckFlow } = useCheckFlowNavigate();

    const layer = useRouteLayer();
    const check = useRouteCheck();
    const sendEmailNotificationMutation = useSendEmailNotificationMutation();

    const config = useConfig();
    const { location, required_images, flowHasRequiredImages, validate } = useCheckSummaryValidation(layer, check);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;

    const { data: offlineFruitImages = {} } = useOfflineFruitImages(tenantId, check.check_id);
    const checkWithOfflineFruitImages = {
        ...check,
        avocados: check.avocados?.map((avocado) => ({
            ...avocado,
            images: offlineFruitImages[avocado.avocado_id] ?? avocado.images,
        })),
    };

    // not sure if this is needed anymore
    // const canSubmit = useMemo(() => {
    //     const formValid = !location?.meta_form_summary || metaFormIsValid(location.meta_form_summary as FormField[], check, false);
    //     const imagesValid = validateRequiredImages(required_images, images || [], false);
    //     return formValid && imagesValid;
    // }, [location?.meta_form_summary, required_images, images, check?.location]);

    // * Validates, then completes the check (locally when offline, via the API when online). Shared by Submit and Next-pallet.
    const completeCheck = async () => {
        if (!validate()) return false;

        if (inOfflineMode) {
            // Offline branch (issue 5.6). Skip the notification email,
            // the business-rules trigger, and the completeCheck dispatch
            // — all three rely on the server. Instead transition the
            // session into ready_to_sync; the Phase 6 replay engine
            // picks it up when connectivity returns.
            const sessionId = getActiveSessionId();
            if (!sessionId) {
                toast.error("Offline check could not be submitted: no active offline session.");
                return false;
            }

            try {
                await markReadyToSync(sessionId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[offline] markReadyToSync failed", err);
                toast.error("Could not save the check locally. Please try again.");
                return false;
            }
            // Leave activeSessionId set. The session has been submitted
            // (status=ready_to_sync) but is still in flight: replay
            // hasn't yet replaced the synthetic temp_check_id with the
            // real server id, and any UI that reads from IDB by the
            // active session (`useCheckImages`, `useFruitImages`, the
            // offline session debug panel) needs the pointer through
            // the in-flight period to keep serving cached data instead
            // of falling back to `/api/images/check/<tempId>/` and
            // hitting a 404. On success the replay engine deletes the row
            // and clears this pointer, so the fallback paths engage; the
            // image hooks additionally resolve the session via
            // `getActiveSession()` rather than trusting the id, because a
            // dangling pointer used to make `addPhoto` throw ("Failed to
            // save Image") and image reads return [] while online. A new
            // scan via `setLayerSelect` overwrites the pointer with the new
            // session id, so this doesn't strand future checks.
            toast.success("Check saved locally. Will sync when connection returns.");
            return true;
        }

        if (location?.enable_send_notification && location?.check_email_recipients && !isEmailSent && (check.check_finished_email_is_sent !== "true")) {
            sendEmailNotificationMutation.mutate({ check });
            setIsEmailSent(true);
        }

        dispatch(triggerCheckBusinessRules(tenantId, check.check_id) as AvosAction);

        // Use callAvosApi directly (not dispatch) so we get a real Promise that
        // rejects on HTTP errors; the axios interceptor in apiClient.tsx has
        // already shown the error toast, so just swallow it here.
        try {
            await callAvosApi(`/checks/${check.check_id}/complete`, { method: "POST" });
        } catch {
            return false;
        }

        return true;
    };

    const submit = async (options?: { nextPallet?: boolean }) => {
        const nextPallet = options?.nextPallet ?? false;
        if (!(await completeCheck())) return;

        if (inOfflineMode) {
            exitCheckFlow();
            return;
        }

        // Check if there is a next step in the flow (e.g. send-external-notification)
        const nextStep = config.next_check_page(layer, check, path_summary);
        if (nextStep) {
            navigateToCheckAction(nextStep.path, true, nextPallet ? { nextPallet: "true" } : undefined);
            return;
        }

        if (nextPallet) {
            return; // ScanLayerButton opens the next-pallet scan modal itself once onClick returns
        }

        if (location?.cta_form_title) {
            navigateToCheckAction(`${params.action}/modal/cta`);
            return;
        }

        exitCheckFlow();
    };

    const buttons = check?.location ? <SummaryBottomButtons submit={submit} completeCheck={completeCheck} /> : null;

    // take box_size from check, if unavaiable take it from layer
    const box_size = check.box_size || layer.box_size;
    const multiple_boxes = box_size > 0 && location?.show_fruit_per_box;

    // when you navigate from a parent layer to a child layer check summary...
    // we need to give it some time to reinstate the config (e.g. when the parent doesn't have a location)
    return (!layer || !check || !location || !location.summary_fruit_fields) ? (
        <Loading />
    ) : (
        <Box buttons={buttons}>
            <PalletSummary />
            <SmartSamplingHistogram/>
            {/* {location.flow.find((f) => f.path === path_visual_check) && <VisualCheckForm show_images={false} is_check_summary={true}/>} */}
            {!multiple_boxes && <BoxItem avocados={checkWithOfflineFruitImages.avocados} check={checkWithOfflineFruitImages} layer={layer} title={`Check ${check.check_id}`} />}
            {multiple_boxes && <BoxedFruitTable check={checkWithOfflineFruitImages} layer={layer} />}
            <CheckSummaryForm check={check} layer={layer} />
            {flowHasRequiredImages && <CheckImages tenant_id={tenantId} check_id={check.check_id} required_images={required_images} />}
            <LayerCtaCheckSummary />
        </Box>
    );
}

export function SummaryBottomButtons({ submit, completeCheck }: { submit(options?: { nextPallet?: boolean }): void; completeCheck(): void }) {
    const check = useRouteCheck();
    const layer = useRouteLayer();
    const user = useSelector((state: RootState) => state.auth.user);
    const params: any = useParams();
    const tenantId = params.tenant_id as string;
    const auth = useAuthorization();
    const config = useConfig();
    const location = config.get_check_type_config(layer, check);
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;
    const unitOfAccount = config.get_check_unit_of_account(layer, check) || "Fruit";
    const { navigateToCheckAction } = useCheckFlowNavigate();
    // No mutation dispatches avocados.isUpdating anymore; a pending fruit mutation is the equivalent signal.
    const isUpdating = useIsMutating() > 0;
    const checkIsUpdating = useIsMutating() > 0;
    const disabled = isUpdating || checkIsUpdating;

    if (!check.location) {
        return null;
    }

    const first = config.first_check_page(layer, check, params.action || "") as any;
    const layerTypeConfig = config.get_raw_layer_config(layer.type);
    const layerTypeLabel = layerTypeConfig?.label || layerTypeConfig?.text || layer.type || "layers";
    const groupByFields = applyDisplayFilterFields(location?.retrospective_group_by_fields, {
        fruit_type: layer.fruit_type,
        layer_type: layer.type,
    });

    return (
        <div className="d-flex flex-wrap gap-2 justify-content-start align-items-center">
            {(user.is_admin || auth.userHasRole(UserRole.TENANT_ADMIN)) && <InvalidCheckModal />}
            {(user.is_admin || auth.userHasRole(UserRole.TENANT_ADMIN)) && <PreCheckFormModal />}
            {(location?.retrospective_group_by_fields?.length || location?.retrospective_group_by_attributes?.length) && location?.retrospective_group_parent_types?.length && <RetrospectiveGroupingModal
                tenantId={tenantId}
                checkId={check.check_id}
                offlineSessionId={inOfflineMode ? getActiveSessionId() : null}
                layerTypeLabel={layerTypeLabel}
                groupByFields={groupByFields}
            />}

            <div className="d-flex gap-2 flex-wrap ms-auto justify-content-md-end align-items-md-center">
                <Button
                    color="secondary"
                    disabled={disabled}
                    outline
                    block
                    onClick={() => navigateToCheckAction("report", false)}
                    icon={faFileExport}
                >
                    Report
                </Button>

                {first && (
                    <Button
                        color="primary"
                        disabled={disabled}
                        outline
                        block
                        onClick={() => navigateToCheckAction(first.path)}
                        icon={faPenToSquare}
                    >
                        Edit {unitOfAccount}
                    </Button>
                )}

                <Button color="primary" disabled={disabled} block onClick={() => submit()} icon={faCheckCircle}>
                    Submit
                </Button>

                {location?.show_scanner_button_on_check_page && (() => {
                    // If there's a next step after summary (e.g. external notification), navigate there with nextPallet flag
                    const nextStep = config.next_check_page(layer, check, path_summary);
                    if (nextStep) {
                        return (
                            <Button
                                color="primary"
                                disabled={disabled}
                                onClick={() => submit({ nextPallet: true })}
                            >
                                Next pallet
                            </Button>
                        );
                    }
                    return (
                        <ScanLayerButton
                            modal
                            label="Next pallet"
                            initialCheckType={check?.location}
                            disabled={disabled}
                            onClick={() => completeCheck()}
                        />
                    );
                })()}
            </div>
        </div>
    );
}
